import { IDP_FRESHNESS_MIN_SEC } from '@/constants/idp-auth';
import { IDP_ACCESS_TOKEN_EXPIRES_COOKIE } from '@/lib/idp-access-token-cookie';

const SYNC_TOKEN_ENDPOINT = '/frontend-api/auth/sync-token';

const SYNC_RETRY_BACKOFF_MS = 10_000;

let cache: { expiresInSec: number; receivedAtMs: number } | null = null;
let inFlight: Promise<void> | null = null;
let disabled = false;
let seeded = false;
let retryNotBeforeMs = 0;

function seedCacheFromCookie(): void {
  const prefix = `${IDP_ACCESS_TOKEN_EXPIRES_COOKIE}=`;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  if (!entry) return;

  const expiresAt = Number(entry.slice(prefix.length));
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return;

  const nowSec = Math.floor(Date.now() / 1000);
  cache = { expiresInSec: Math.max(0, expiresAt - nowSec), receivedAtMs: Date.now() };
}

function estimatedRemainingSec(): number | null {
  if (!cache) return null;
  const elapsedSec = (Date.now() - cache.receivedAtMs) / 1000;

  if (elapsedSec < 0) return null;
  return cache.expiresInSec - elapsedSec;
}

async function runSyncToken(): Promise<void> {
  try {
    const res = await fetch(SYNC_TOKEN_ENDPOINT, { cache: 'no-store' });

    if (res.status === 204) {
      disabled = true;
      cache = null;
      return;
    }

    if (!res.ok) {
      cache = null;
      retryNotBeforeMs = Date.now() + SYNC_RETRY_BACKOFF_MS;
      return;
    }

    const data = (await res.json()) as { ok: boolean; expiresInSec?: number };
    if (data.ok && typeof data.expiresInSec === 'number') {
      cache = { expiresInSec: data.expiresInSec, receivedAtMs: Date.now() };
      retryNotBeforeMs = 0;
    } else {
      cache = null;
      retryNotBeforeMs = Date.now() + SYNC_RETRY_BACKOFF_MS;
    }
  } catch {
    cache = null;
    retryNotBeforeMs = Date.now() + SYNC_RETRY_BACKOFF_MS;
  }
}

export async function ensureIdpTokenFresh(): Promise<void> {
  if (disabled) return;

  if (!cache && !seeded) {
    seeded = true;
    seedCacheFromCookie();
  }

  const remaining = estimatedRemainingSec();
  if (remaining !== null && remaining >= IDP_FRESHNESS_MIN_SEC) return;

  if (Date.now() < retryNotBeforeMs) return;

  inFlight ??= runSyncToken().finally(() => {
    inFlight = null;
  });
  await inFlight;
}

export function resetIdpTokenGate(): void {
  cache = null;
  inFlight = null;
  retryNotBeforeMs = 0;
}
